// src/App.tsx
import React from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import ScrollToTop from "./pages/ScrollToTop";
import ScrollToggle from "./pages/ScrollToggle";
import { ThemeProvider } from "./context/ThemeContext";
import { AssessmentProvider } from "./context/AssessmentContext";
import { AuthProvider } from "./context/AuthContext";
import { AdminProvider } from "./context/AdminContext";
import GlobalStyles from "./components/GlobalStyles/GlobalStyles";
import { useAuth } from "./context/AuthContext";
import { WalkthroughProvider } from "./walkthrough/WalkthroughProvider";
import { ExamProvider } from "./context/ExamContext";
// import Home from "./pages/Home";

const Blog = React.lazy(() => import("./pages/Blog"));
const BlogPost = React.lazy(() => import("./pages/BlogPost"));
const AboutUs = React.lazy(() => import("./pages/AboutUs"));
const LeadForm = React.lazy(() => import("./pages/LeadForm"));
const TermsOfService = React.lazy(
  () => import("./pages/policies/TermsOfService"),
);
const PrivacyPolicy = React.lazy(
  () => import("./pages/policies/PrivacyPolicy"),
);
const CookiePolicy = React.lazy(() => import("./pages/policies/CookiePolicy"));
const GDPRCompliance = React.lazy(
  () => import("./pages/policies/GDPRCompliance"),
);
const TrademarkPolicy = React.lazy(
  () => import("./pages/policies/TrademarkPolicy"),
);
const AntiSpamPolicy = React.lazy(
  () => import("./pages/policies/AntiSpamPolicy"),
);
const Compliance = React.lazy(() => import("./pages/policies/Compliance"));
const IPRComplaints = React.lazy(
  () => import("./pages/policies/IPRComplaints"),
);
const Security = React.lazy(() => import("./pages/policies/Security"));
const Videos = React.lazy(() => import("./pages/Videos"));
const VideoPlayer = React.lazy(() => import("./pages/VideoPlayer"));
const ContactUs = React.lazy(() => import("./pages/policies/ContactUs"));
const EnterpriseContactForm = React.lazy(
  () => import("./pages/EnterpriseContactForm"),
);

const LoginPage = React.lazy(() => import("./pages/ott/LoginPage"));
const Dashboard = React.lazy(() => import("./pages/ott/Dashboard"));
const AllCourses = React.lazy(() => import("./pages/ott/AllCourses"));
const ChapterPage = React.lazy(() => import("./pages/ott/ChapterPage"));
const WatchPage = React.lazy(() => import("./pages/ott/WatchPage"));
const TopicPage = React.lazy(() => import("./pages/ott/TopicPage"));
const LearningPath = React.lazy(() => import("./pages/ott/LearningPath"));
const NotesPage = React.lazy(() => import("./pages/ott/NotesPage"));
const ReportsPage = React.lazy(() => import("./pages/ott/ReportsPage"));
const CommunityPage = React.lazy(() => import("./pages/ott/CommunityPage"));
const ProfilePage = React.lazy(() => import("./pages/ott/ProfilePage"));
const NotFound = React.lazy(() => import("./pages/ott/NotFound"));
// import QuizPage from "./pages/QuizPage/QuizPage";
const SubscriptionPage = React.lazy(
  () => import("./pages/ott/SubscriptionPage"),
);
const PaymentComponent = React.lazy(
  () => import("./pages/payment/PaymentComponent"),
);
const PaymentStatus = React.lazy(() => import("./pages/payment/PaymentStatus"));

const Landing = React.lazy(() => import("./pages/assessment/Landing"));
const Setup = React.lazy(() => import("./pages/assessment/Setup"));
const Practice = React.lazy(() => import("./pages/assessment/Practice"));
const Test = React.lazy(() => import("./pages/assessment/Test"));
const Report = React.lazy(() => import("./pages/assessment/Report"));
const RefundCancellationPolicyPage = React.lazy(
  () => import("./pages/policies/RefundAndCancellationPolicy"),
);
const GrievanceRedressalPolicyPage = React.lazy(
  () => import("./pages/policies/GrievanceRedressalPolicy"),
);
const DeliveryPolicyPage = React.lazy(
  () => import("./pages/policies/DeliveryPolicy"),
);
const FirstTimePasswordChange = React.lazy(
  () => import("./pages/ott/PasswordChange"),
);
const SettingsPage = React.lazy(() => import("./pages/ott/SettingsPage"));
const AccountDelete = React.lazy(() => import("./pages/ott/AccountDelete"));
const SchoolOttUsageReports = React.lazy(
  () => import("./pages/schooladmin/SchoolOttUsageReports"),
);
const FAQ = React.lazy(() => import("./pages/FAQ"));

const AllContinueWatching = React.lazy(() =>
  import("./pages/ott/AllContinueWatching").then((m) => ({
    default: m.AllContinueWatching,
  })),
);

const AdminSignIn = React.lazy(() => import("./pages/admin/AdminSignIn"));
const AdminDashboard = React.lazy(() => import("./pages/admin/AdminDashboard"));
const LandingPage = React.lazy(() => import("./pages/LandingPage"));

const PromoVideo = React.lazy(() => import("./pages/promos/PromoVideo"));
const Brochure = React.lazy(() => import("./pages/promos/Brochure"));
const UserGuideWeb = React.lazy(() => import("./pages/userGuide/UserGuideWeb"));
const UserGuideApp = React.lazy(() => import("./pages/userGuide/UserGuideApp"));

const LearningLandingPage = React.lazy(
  () => import("./pages/learning/LearningLandingPage"),
);
const StudyLandingPage = React.lazy(
  () => import("./pages/study/StudyLandingPage"),
);

const LearningThanksPage = React.lazy(
  () => import("./pages/learning/LearningThanks"),
);
const StudyThanksPage = React.lazy(() => import("./pages/study/StudyThanks"));

const ExamDetailsScreen = React.lazy(() => import("./pages/exam/ExamDetailsScreen"));
const ExamQuestionScreen = React.lazy(
  () => import("./pages/exam/ExamQuestionScreen"),
);
const ExamResultScreen = React.lazy(
  () => import("./pages/exam/ExamResultScreen"),
);
const ExamReviewScreen = React.lazy(
  () => import("./pages/exam/ExamReviewScreen"),
);

function ClarityBootstrap() {
  React.useEffect(() => {
    const clarityEnv = String(process.env.REACT_APP_ENV || "").toLowerCase();
    const clarityProjectId = process.env.REACT_APP_CLARITY_PROJECT_ID || "";
    const clarityEnabledEnvironments = new Set(["staging", "production"]);

    if (!clarityEnabledEnvironments.has(clarityEnv) || !clarityProjectId) {
      return;
    }

    const existingScript = document.getElementById("ms-clarity-script");
    if (existingScript) {
      return;
    }

    ((c: any, l: Document, a: string, r: string, i: string) => {
      c[a] =
        c[a] ||
        function (...args: any[]) {
          (c[a].q = c[a].q || []).push(args);
        };

      const t = l.createElement(r) as HTMLScriptElement;
      t.async = true;
      t.id = "ms-clarity-script";
      t.src = `https://www.clarity.ms/tag/${i}`;

      const y = l.getElementsByTagName(r)[0] as HTMLElement | undefined;
      if (y?.parentNode) {
        y.parentNode.insertBefore(t, y);
      } else {
        l.head.appendChild(t);
      }
    })(window as any, document, "clarity", "script", clarityProjectId);
  }, []);

  return null;
}

function AuthRouteShell() {
  const { isLoading } = useAuth();
  const location = useLocation();
  const isRedirecting = sessionStorage.getItem("authRedirecting") === "1";

  React.useEffect(() => {
    if (location.pathname === "/" || location.pathname === "/login") {
      sessionStorage.removeItem("logoutInProgress");
    }
    if (!isLoading && isRedirecting) {
      sessionStorage.removeItem("authRedirecting");
    }
  }, [isLoading, isRedirecting, location.pathname]);

  return (
    <>
      <ScrollToggle />
      <ScrollToTop />
      <GlobalStyles />
      <React.Suspense
        fallback={
          <div
            style={{
              minHeight: "60vh",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontFamily: "inherit",
            }}
          >
            Loading...
          </div>
        }
      >
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/about-us" element={<AboutUs />} />
          <Route path="/contact" element={<LeadForm />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />
          <Route path="/gdpr-compliance" element={<GDPRCompliance />} />
          <Route path="/trademark-policy" element={<TrademarkPolicy />} />
          <Route path="/anti-spam-policy" element={<AntiSpamPolicy />} />
          <Route path="/compliance" element={<Compliance />} />
          <Route path="/ipr-complaints" element={<IPRComplaints />} />
          <Route path="/security" element={<Security />} />
          <Route
            path="/refund-and-cancellation-policy"
            element={<RefundCancellationPolicyPage />}
          />
          <Route
            path="/grievance-redressal-policy"
            element={<GrievanceRedressalPolicyPage />}
          />
          <Route path="/delivery-policy" element={<DeliveryPolicyPage />} />
          <Route path="/contact-us" element={<ContactUs />} />
          <Route
            path="/enterprise-contact"
            element={<EnterpriseContactForm />}
          />
          <Route path="/videos" element={<Videos />} />
          <Route path="/video-player" element={<VideoPlayer />} />
          <Route path="/auth/callback" element={<LoginPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/home/courses" element={<AllCourses />} />
          <Route path="/home/courses/:courseSlug" element={<AllCourses />} />
          <Route
            path="/home/courses/:courseSlug/:classSlug"
            element={<AllCourses />}
          />
          <Route
            path="/home/courses/:courseSlug/:classSlug/:subjectSlug"
            element={<AllCourses />}
          />
          <Route
            path="/home/courses/:courseSlug/:classSlug/:subjectSlug/:chapterSlug"
            element={<AllCourses />}
          />
          <Route path="/chapter/:chapterNumber" element={<ChapterPage />} />
          <Route
            path="/watch/:chapterNumber/:videoId"
            element={<WatchPage />}
          />
          <Route path="/topic/:topicId" element={<TopicPage />} />
          <Route path="/learning-path" element={<LearningPath />} />
          {/* <Route path="/quiz" element={<QuizPage />} /> */}
          <Route path="/notes" element={<NotesPage />} />
          <Route path="/reports" element={<ReportsPage />} />
          <Route path="/school-admin" element={<SchoolOttUsageReports />} />
          <Route
            path="/school-admin/reports"
            element={<SchoolOttUsageReports />}
          />

          <Route path="/community" element={<CommunityPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/account-delete" element={<AccountDelete />} />
          <Route
            path="/change-password"
            element={<FirstTimePasswordChange />}
          />
          <Route path="/subscription" element={<SubscriptionPage />} />
          <Route path="/payment" element={<PaymentComponent />} />
          <Route
            path="/payment-status"
            element={<PaymentStatus paymentId={""} />}
          />
          <Route path="/landing" element={<Landing />} />
          <Route path="/setup" element={<Setup />} />
          <Route path="/practice" element={<Practice />} />
          <Route path="/test" element={<Test />} />
          <Route path="/report/:id" element={<Report />} />
          <Route path="/faq" element={<FAQ />} />
          <Route
            path="/continue-watching/all"
            element={<AllContinueWatching />}
          />
          <Route path="/admin/sign-in" element={<AdminSignIn />} />
          <Route path="/admin/dashboard" element={<AdminDashboard />} />

          <Route path="/promo-video" element={<PromoVideo />} />
          <Route path="/brochure" element={<Brochure />} />
          <Route path="/user-guide-web" element={<UserGuideWeb />} />
          <Route path="/user-guide-app" element={<UserGuideApp />} />

          <Route path="/e-learning" element={<LearningLandingPage />} />
          <Route path="/study-online" element={<StudyLandingPage />} />
          <Route path="/e-learning/thanks" element={<LearningThanksPage />} />
          <Route path="/study-online/thanks" element={<StudyThanksPage />} />

          <Route path="/exam" element={<ExamDetailsScreen />} />
          <Route path="/exam/question" element={<ExamQuestionScreen />} />
          <Route path="/exam/result" element={<ExamResultScreen />} />
          <Route path="/exam/review" element={<ExamReviewScreen />} />

          <Route path="*" element={<NotFound />} />
        </Routes>
      </React.Suspense>
    </>
  );
}

export default function App() {
  return (
    <AssessmentProvider>
      <ExamProvider>
        <BrowserRouter>
          <ThemeProvider>
            <AuthProvider>
              <WalkthroughProvider>
                <AdminProvider>
                  <ClarityBootstrap />
                  <AuthRouteShell />
                </AdminProvider>
              </WalkthroughProvider>
            </AuthProvider>
          </ThemeProvider>
        </BrowserRouter>
      </ExamProvider>
    </AssessmentProvider>
  );
}
